import { getFullPageName } from './config'
import { setPageInfo, setContentInfo } from './dataLayer'

// Adobe Launch loads async, so `_satellite` may not exist yet when the first
// route mounts. Page views fired before it's ready are queued and flushed
// once `_satellite.track` becomes available (checked every 500ms, up to 30s).
type QueuedPageView = {
  pageKey: string
  pageData?: {
    pageTemplate?: string
    pageType?: string
    contentTitle?: string
    contentType?: string
  }
}

const pageViewQueue: QueuedPageView[] = []
let queueFlushTimer: ReturnType<typeof setInterval> | null = null

const fireCustomPageView = (pageKey: string): void => {
  const satellite = globalThis.window?._satellite
  if (!satellite) return

  if (typeof satellite.kpCustomEvent === 'function') {
    satellite.kpCustomEvent('cjaCustomPageview', {
      pageName: pageKey,
    })
    return
  }

  if (typeof satellite.track === 'function') {
    satellite.track('cjaCustomPageview')
  }
}

const flushQueueWhenReady = (): void => {
  if (queueFlushTimer) return

  const startTime = Date.now()
  queueFlushTimer = setInterval(() => {
    if (globalThis.window?._satellite?.track || globalThis.window?._satellite?.kpCustomEvent) {
      while (pageViewQueue.length > 0) {
        const queuedPageView = pageViewQueue.shift()
        if (queuedPageView) {
          trackPageView(queuedPageView.pageKey, queuedPageView.pageData)
        }
      }
      clearInterval(queueFlushTimer!)
      queueFlushTimer = null
    } else if (Date.now() - startTime > 30000) {
      pageViewQueue.length = 0
      clearInterval(queueFlushTimer!)
      queueFlushTimer = null
    }
  }, 500)
}

/** Updates the data layer and fires a CJA page-view event for the given page key. */
export const trackPageView = (
  pageKey: string,
  pageData?: {
    pageTemplate?: string
    pageType?: string
    contentTitle?: string
    contentType?: string
  }
): void => {
  try {
    const pageName = getFullPageName(pageKey)

    setPageInfo(pageName, pageData)
    setContentInfo({
      title: pageData?.contentTitle || pageKey,
      type: pageData?.contentType || 'page',
    })

    if (globalThis.window !== undefined && (globalThis.window._satellite?.track || globalThis.window._satellite?.kpCustomEvent)) {
      fireCustomPageView(pageKey)
    } else {
      pageViewQueue.push({ pageKey, pageData })
      flushQueueWhenReady()
    }
  } catch (error) {
    console.error('CJA: error tracking page view', error)
  }
}
